import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useMemo } from 'react';
import { useUpdateNodeInternals } from 'reactflow';

import {
  TaskDependencyError,
  selectCodeTaskDataInputHandlesByTaskID,
  selectCodeTaskDataOutputHandlesByTaskID,
  selectConditionTaskDataInputHandlesByTaskID,
  selectConditionTaskLabwareOutputHandlesByTaskID,
  selectDataExportTaskDataInputHandlesByTaskID,
  selectDataExportTaskOutputHandlesByTaskID,
  selectDataInputHandlesByTaskID,
  selectDataOutputHandlesByTaskID,
} from '../../../../features/selectors';
import type { WorkflowSerializationError } from '../../../../features/workflowJsonSelectors';
import { useAppSelector } from '../../../../hooks';
import type { Task } from '../../../../hooks/tasks';
import type { AnnotatedError } from '../../../../text/errorsToUserCopy';
import { TASK_DEPENDENCY_ERRORS_TEXT } from '../../../../text/taskDependencyErrors';
import {
  LabwareDataForTask,
  LabwareDataForTaskByHandlePairId,
  PropertiesType,
} from '../../../../types';
import { getTaskCanAddMoreLabware } from '../../../../utils';
import uuid from '../../../../uuid';
import { taskDependencyIcon } from '../../../customIcons/misc';
import { Props as NodeIndicatorProps } from '../../NodeIndicators/NodeIndicator';

type LabwareHandleComponentData =
  | {
      handleType: 'labware';
      labwareData: LabwareDataForTask;
      handlePairId: string;
    }
  | {
      handleType: 'addLabware';
      handlePairId: string;
    };

type Props = {
  taskId: string;
  taskType: string;
  instrumentCapacity: number;
  labwareDataByHandle: LabwareDataForTaskByHandlePairId;
  orderedHandlePairIds: string[];
};

type ActionTaskDataHandleProps = {
  taskId: string;
};

type DataTaskHandlesProps = {
  taskId: string;
  propertiesType: PropertiesType.Code | PropertiesType.DataExport;
};

type ConditionTaskHandlesProps = {
  taskId: string;
};

export const useLabwareHandleComponents = ({
  taskId,
  taskType,
  instrumentCapacity,
  labwareDataByHandle,
  orderedHandlePairIds,
}: Props) => {
  const updateNodeInternals = useUpdateNodeInternals();

  const labwareLabelledHandleComponents: LabwareHandleComponentData[] =
    useMemo(() => {
      updateNodeInternals(taskId);

      const handleComponents = orderedHandlePairIds.map(
        (handlePairId): LabwareHandleComponentData => {
          const labwareData = labwareDataByHandle[handlePairId];
          if (labwareData == null) {
            // No labware, but we have a handlePair, so it must be Add Labware.
            // NOT a uuid, use the handlePairId.
            return { handleType: 'addLabware', handlePairId };
          }

          return { handleType: 'labware', labwareData, handlePairId };
        }
      );

      const canAddMoreLabware = getTaskCanAddMoreLabware({
        taskType,
        instrumentCapacity,
        connectedLabwareCount: orderedHandlePairIds.length,
      });

      if (canAddMoreLabware) {
        // if there is space, add one empty "Add Labware" handle at the end with a new handle UIID
        handleComponents.push({
          handleType: 'addLabware',
          handlePairId: uuid(),
        });
      }
      return handleComponents;
    }, [
      instrumentCapacity,
      taskId,
      taskType,
      labwareDataByHandle,
      orderedHandlePairIds,
      updateNodeInternals,
    ]);

  return labwareLabelledHandleComponents;
};

/**
 * Gets inputs/outputs for an action task and merges them.
 *
 * @param taskId - task id.
 *
 * @returns an object with inputs/outputs for an action task that works with data.
 */
export const useActionTaskDataHandleComponents = ({
  taskId,
}: ActionTaskDataHandleProps) => {
  const dataHandleOutputs = useAppSelector(selectDataOutputHandlesByTaskID)[
    taskId
  ];
  const dataHandleInputs = useAppSelector(selectDataInputHandlesByTaskID)[
    taskId
  ];

  return {
    inputs: dataHandleInputs ?? [],
    outputs: dataHandleOutputs ?? [],
  };
};

/**
 * Gets inputs/outputs for a task and merges them.
 *
 * @param taskId - task id.
 * @param propertiesType - task type.
 *
 * @returns an object with inputs/outputs for a task that works with data.
 */
export const useDataTaskHandleComponents = ({
  taskId,
  propertiesType,
}: DataTaskHandlesProps) => {
  const dataHandleInputs = useAppSelector(
    propertiesType === PropertiesType.Code
      ? selectCodeTaskDataInputHandlesByTaskID
      : selectDataExportTaskDataInputHandlesByTaskID
  )[taskId];

  const dataHandleOutputs = useAppSelector(
    propertiesType === PropertiesType.Code
      ? selectCodeTaskDataOutputHandlesByTaskID
      : selectDataExportTaskOutputHandlesByTaskID
  )[taskId];

  return {
    inputs: dataHandleInputs ?? [],
    outputs: dataHandleOutputs ?? [],
  };
};

export const useConditionTaskHandleComponents = ({
  taskId,
}: ConditionTaskHandlesProps) => {
  const dataHandleInputs = useAppSelector(
    selectConditionTaskDataInputHandlesByTaskID
  )[taskId];

  const labwareHandleOutputs = useAppSelector(
    selectConditionTaskLabwareOutputHandlesByTaskID
  )[taskId];

  return {
    inputs: dataHandleInputs ?? [],
    outputs: labwareHandleOutputs ?? [],
  };
};

export const getIndicators = ({
  task,
  taskDependencyErrors,
  workflowLevelErrors,
}: {
  task: Task;
  taskDependencyErrors: Record<string, TaskDependencyError[]> | null;
  workflowLevelErrors: AnnotatedError<WorkflowSerializationError>[];
}): NodeIndicatorProps[] => {
  const indicators: NodeIndicatorProps[] = [];

  const errText: string[] = [];

  // relevant workflow-level errors
  for (const err of workflowLevelErrors) {
    errText.push(err.description);
  }

  for (const errs of Object.values(taskDependencyErrors ?? {})) {
    for (const err of errs) {
      errText.push(TASK_DEPENDENCY_ERRORS_TEXT[err]);
    }
  }

  if (errText.length > 0) {
    indicators.push({
      icon: WarningAmberIcon,
      iconColor: 'error',
      message: errText,
    });
  }

  // data indicators (not related to errors)
  const taskHasDependencyRelationship =
    [...task.dependencies, ...task.dependents].length > 0;
  if (taskHasDependencyRelationship) {
    indicators.push({
      icon: taskDependencyIcon,
      message: 'Task has dependencies.',
    });
  }

  return indicators;
};
